import React, {
  useState,
  useEffect,
  useRef,
  RefObject,
} from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import defer from 'lodash/defer';
import ArrowRightIcon from '@mui/icons-material/ArrowRightAltRounded';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import Controller from 'core/components/FormController';
import PanelExtension from 'core/components/PanelExtension';
import ToolTip from 'core/components/Tooltip';
import CollapseIcon from 'core/icons/collapse';
import {
  AppointmentPaymentMethodDTO,
  PatientDTO,
  PatientPaymentRequestDTO,
  SquareTerminalPaymentMethodDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import PaymentFields, {
  TerminalPaymentOption,
} from 'views/EMR/Billing/PaymentFields';
import SummaryItem from 'views/EMR/Billing/SummaryItem';
import UnpaidEncounters from 'views/EMR/Billing/UnpaidEncounters';
import {
  useGetBillingSummary,
  useProcessPayment,
  useSendOnlinePaymentRequest,
} from 'views/EMR/hooks/billing';
import {
  totalBillReviewColumns,
} from 'views/EMR/Billing/columns';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import PatientSelect from 'pages/Dashboard/pages/Todos/components/PatientSelect';
import {
  useNotify,
} from 'core/hooks';
import {
  appointmentsEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  isNilOrZero,

  formatMoney,
} from '@populateui/utils';
import CurrencyTextField from 'core/components/CurrencyTextField';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import SwitchButton from 'core/components/Switch';
import InsuranceIcon from 'core/icons/insurance';
import Alert from 'pages/Dashboard/components/Alert';
import sum from 'lodash/sum';
import {
  useFormAutoFocus,
} from 'views/EMR/hooks/common';

export type DialogParams = {
  patient: Partial<PatientDTO>;
  encounterId?: number;
  amount?: number;
  isDeposit?: boolean;
};

type Props = DialogParams & {
  onClose: () => void;
};

export type PaymentForm = Omit<PatientPaymentRequestDTO, 'discriminator' | 'isCardPresent' | 'deviceId'> & {
  paymentMethod?: AppointmentPaymentMethodDTO | null;
  patient?: Partial<PatientDTO>;
  email?: string;
  isCardPresent?: TerminalPaymentOption;
};

type CollectPaymentFormProps = {
  isDeposit?: boolean;
  encounterId?: number;
  onClose: () => void;
  onExpand: () => void;
  patient?: Partial<PatientDTO>;
};

function validatePaymentAmount(value: number, formValues: PaymentForm) {
  const totalPayment = sum([
    Number(value ?? 0),
    Number(formValues.depositPayment ?? 0),
    Number(formValues.pastDuePayment ?? 0),
  ]);

  return totalPayment === 0 ? 'Payment amount must be greater than $0.00' : true;
}

function CollectPaymentForm({
  isDeposit = false,
  encounterId,
  patient,
  onClose,
  onExpand,
}: CollectPaymentFormProps) {
  const patientInputRef = useFormAutoFocus({ enabled: isNilOrZero(encounterId) });
  const depositInputRef = useFormAutoFocus({ enabled: isDeposit });
  const pastDueInputRef = useRef<HTMLInputElement>(null);
  const notify = useNotify();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    watch,
    setValue,
    trigger,
  } = useFormContext();

  const [paymentMethod, depositPayment, pastDuePayment] = watch(['paymentMethod', 'depositPayment', 'pastDuePayment']);

  const isTerminal = !isNil((paymentMethod as SquareTerminalPaymentMethodDTO)?.deviceId);

  const patientId = patient?.patientId ?? 0;
  const { data, isFetching } = useGetBillingSummary({
    encounterId,
    patientId,
    isDeposit,
  });

  const {
    copayPaid = 0,
    copayTotal = 0,
    insuranceName,
    due = 0,
    totalBalance = 0,
    insuranceCopay = 0,
  } = data ?? {};

  useEffect(() => {
    if (isNil(patient)) {
      defer(() => { patientInputRef?.current?.focus?.(); });
    }
  }, [patient]);

  const onPaymentComplete = () => {
    if (!isTerminal) {
      notify({
        message: `${isDeposit ? 'Deposit' : 'Payment'} was collected.`,
        variant: 'success',
      });
    }

    appointmentsEventChannel.emit('onEventUpdated', encounterId);
    paymentsEventChannel.emit('onPaymentsDataChanged', 'patient-payments');
    paymentsEventChannel.emit('onPatientBalanceChange');
    onClose();
  };

  const {
    createPayment,
    cancelCurrentPayment,
    cancelling,
    isCancelable,
  } = useProcessPayment({
    onPaymentComplete,
    paymentMethod,
  });

  const { sendOnlinePaymentRequest } = useSendOnlinePaymentRequest();

  const onSubmit = async (data: PaymentForm) => {
    if (/^online/i.test(paymentMethod?.discriminator ?? '')) {
      try {
        const {
          comment,
          email,
          currentEncounterPayment,
          includesCopay,
          depositPayment,
          pastDuePayment,
        } = data;
        await sendOnlinePaymentRequest({
          patientId: patientId ?? 0,
          encounterId: encounterId ?? null,
          comment,
          email,
          ...(isNil(currentEncounterPayment) ? {} : { currentEncounterPayment, includesCopay }),
          ...(isNil(depositPayment) ? {} : { depositPayment }),
          ...(isNil(pastDuePayment) ? {} : { pastDuePayment }),
        }, isDeposit ? 'Deposit' : 'Payment');
        onClose();
      } catch (error) {
        console.error(error);
      }
    } else {
      await createPayment({
        ...data,
        patientId: patientId ?? 0,
        encounterId: encounterId ?? null,
      });
    }
  };

  const copayNotSet = copayTotal === 0;
  const isCopayPaid = copayPaid > 0;

  const copayDisabled = copayNotSet || copayPaid >= copayTotal;
  const copayDisabledTooltip = copayNotSet ? 'Copay is not set.' : 'Copay is already collected.';

  useEffect(() => {
    setValue('includesCopay', !(isNil(insuranceName) || copayDisabled));
  }, [copayDisabled, insuranceName]);

  return (
    <div className="flex min-h-0 w-full max-w-[560px] shrink-0 flex-col gap-form-row py-4">
      <div className="shrink-0 w-full flex justify-between items-start px-4">
        <h3 className="text-lg font-semibold">
          Collect
          {' '}
          {isDeposit ? 'deposit' : 'payment'}
        </h3>
        <IconButton
          size="small"
          onClick={onClose}
        >
          <CloseIcon className="!w-5 !h-5 !text-inherit" />
        </IconButton>
      </div>
      <Controller
        control={control}
        name="patient"
        rules={{ required: 'Missing Patient.' }}
        render={({ field, fieldState: { invalid } }) => (
          <PatientSelect
            {...field}
            className="!mb-0 px-4"
            placeholder="Enter patient name"
            required
            error={invalid}
            id="patient-for-payment"
            allowDefault
            readonly={!isNilOrZero(encounterId)}
            inputRef={patientInputRef as RefObject<HTMLInputElement>}
          />
        )}
      />
      <div className="flex-1 min-h-0 w-full flex flex-col overflow-y-auto gap-form-row px-4">
        {patientId !== 0 && (
          <>
            {(!isDeposit && !isNil(insuranceName)) && (
              <Alert
                open
                className="!p-0 !w-full"
                classes={{ message: '!p-2 w-full' }}
                icon={false}
                severity={isCopayPaid ? 'success' : 'info'}
              >
                <div className="flex items-center gap-2 justify-between text-xs w-full">
                  <ToolTip
                    title={`Insurance: ${insuranceName}`}
                    placement="top-start"
                    arrow={false}
                  >
                    <div className="cursor-pointer flex items-center flex-1 gap-2 overflow-hidden">
                      <InsuranceIcon className="!w-5 !h-5" />
                      <div className="flex-1 truncate">
                        {insuranceName}
                      </div>
                    </div>
                  </ToolTip>
                  <strong className="font-semibold w-fit">
                    Copay
                    {isCopayPaid ? ' collected' : null}
                    {': '}
                    {formatMoney(isCopayPaid ? copayPaid : insuranceCopay)}
                  </strong>
                </div>
              </Alert>
            )}
            <div className="flex flex-col w-full gap-2">
              <div className="flex justify-between w-full gap-4">
                {!isDeposit && (
                  <SummaryItem
                    value={due}
                    isLoading={isFetching}
                    title="Appointment due"
                  />
                )}
                <SummaryItem
                  value={totalBalance}
                  isLoading={isFetching}
                  title="Balance overdue"
                />
              </div>
              {totalBalance > 0 && (
                <div className="text-xs">
                  <Button
                    variant="text"
                    color="primary"
                    onClick={onExpand}
                  >
                    See why patient owes this
                    {' '}
                    <ArrowRightIcon className="!w-4 !h-4 !text-inherit" />
                  </Button>
                </div>
              )}
            </div>
            <div className="flex gap-form-row flex-col w-full populate-form">
              <div className="flex w-full flex-col gap-form-row">
                {!isDeposit && (
                  <div className="flex w-full gap-form-field">
                    <Controller
                      control={control}
                      name="currentEncounterPayment"
                      rules={{ validate: validatePaymentAmount }}
                      render={({ field }) => (
                        <Wrapper
                          id="payment-amount"
                          label="Current appointment payment"
                          className="w-1/2"
                        >
                          <CurrencyTextField
                            {...field}
                            allowNegative={false}
                            className="w-full"
                          />
                        </Wrapper>
                      )}
                    />
                    {!isNil(insuranceName) && (
                      <Controller
                        control={control}
                        name="includesCopay"
                        render={({ field }) => (
                          <SwitchButton
                            {...field}
                            classes={{ rootContainer: 'mt-4' }}
                            checked={field.value}
                            label="Includes copay"
                            disabled={copayDisabled || isFetching}
                            tooltip={copayDisabled
                              ? copayDisabledTooltip
                              : 'When enabled, payment will be applied first to copay, then applied to other services.'}
                          />
                        )}
                      />
                    )}
                  </div>
                )}
                {(isNil(depositPayment) && !isDeposit) ? (
                  <Button
                    variant="text"
                    onClick={() => {
                      setValue('depositPayment', 0);
                      defer(() => { depositInputRef?.current?.focus?.(); });
                    }}
                    color="primary"
                    className="flex items-center gap-1 !text-xs !font-medium"
                  >
                    <AddIcon className="!w-5 !h-5 !text-inherit" />
                    Add deposit
                  </Button>
                ) : (
                  <Controller
                    control={control}
                    name="depositPayment"
                    rules={{ validate: isDeposit ? validatePaymentAmount : undefined }}
                    render={({ field }) => (
                      <Wrapper
                        id="deposit-amount"
                        label="Deposit"
                        className="w-1/2"
                        classes={{ container: '!items-end' }}
                      >
                        <CurrencyTextField
                          {...field}
                          allowNegative={false}
                          className="w-full"
                          inputRef={depositInputRef as RefObject<HTMLInputElement>}
                        />
                        {!isDeposit && (
                          <IconButton
                            size="small"
                            className="!h-7 !w-7 relative -top-1"
                            onClick={() => {
                              setValue('depositPayment', undefined);
                              trigger();
                            }}
                          >
                            <CloseIcon className="!w-5 !h-5 !text-gray-500" />
                          </IconButton>
                        )}
                      </Wrapper>
                    )}
                  />
                )}
                {isNil(pastDuePayment) ? (
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => {
                      setValue('pastDuePayment', 0);
                      defer(() => { pastDueInputRef?.current?.focus?.(); });
                    }}
                    className="flex items-center gap-1 !text-xs !font-medium"
                  >
                    <AddIcon className="!w-5 !h-5 !text-inherit" />
                    Add payment for past dues
                  </Button>
                ) : (
                  <Controller
                    control={control}
                    name="pastDuePayment"
                    render={({ field }) => (
                      <Wrapper
                        id="past-due-amount"
                        label="Past dues payment"
                        className="w-1/2"
                        classes={{ container: '!items-end' }}
                      >
                        <CurrencyTextField
                          {...field}
                          inputRef={pastDueInputRef as RefObject<HTMLInputElement>}
                          allowNegative={false}
                          className="w-full"
                        />
                        <IconButton
                          size="small"
                          className="!h-7 !w-7 relative -top-1"
                          onClick={() => {
                            setValue('pastDuePayment', undefined);
                            trigger();
                          }}
                        >
                          <CloseIcon className="!w-5 !h-5 !text-gray-500" />
                        </IconButton>
                      </Wrapper>
                    )}
                  />
                )}
              </div>
              <PaymentFields />
              <Controller
                control={control}
                name="comment"
                render={({ field }) => (
                  <TextAreaWithActions
                    {...field}
                    placeholder="Add comment"
                    rows={3}
                    id="payment-comment"
                    trackingLabel="Payment Comment"
                  />
                )}
              />
            </div>
          </>
        )}
      </div>
      <div className="shrink-0 flex flex-row-reverse justify-between px-4">
        {isCancelable ? (
          <Button
            variant="contained"
            color="primary"
            onClick={cancelCurrentPayment}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling...' : `Cancel ${isDeposit ? 'deposit' : 'payment'}`}
          </Button>
        ) : (
          <DisabledFormTooltip>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isValid || isSubmitting || isFetching}
              onClick={handleSubmit(onSubmit)}
            >
              Confirm
            </Button>
          </DisabledFormTooltip>
        )}
        <Button
          variant="outlined"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </div>
  );
}

export default function PaymentDialog({
  patient,
  onClose,
  encounterId,
  amount = 0,
  isDeposit = false,
}: Props) {
  const form = useForm<PaymentForm>({
    mode: 'all',
    defaultValues: {
      patient,
      currentEncounterPayment: isDeposit ? undefined : amount,
      pastDuePayment: undefined,
      depositPayment: isDeposit ? amount : undefined,
      includesCopay: true,
    },
  });
  const [expanded, setExpanded] = useState<boolean>(false);

  const { watch } = form;

  const selectedPatient = watch('patient');
  const patientId = selectedPatient?.patientId ?? 0;

  return (
    <Dialog
      open
      onClose={onClose}
      showActions={false}
      className={clsx(
        '!space-y-0 overflow-y-hidden transition-[width] !p-0 max-w-[calc(100vw-24px)]',
        {
          '!w-140': !expanded,
          '!w-[1200px]': expanded,
        },
      )}
      classes={{
        root: 'flex max-h-[calc(100vh-24px)] min-h-0 flex-col overflow-hidden',
        bodyRoot: '!overflow-hidden',
      }}
      maxWidth="lg"
      disableEscapeKeyDown
      disableBackdropClick
      autoFocusOptions={{ enabled: false }}
      hideCloseBtn
      title={null}
    >
      <div className="flex min-h-0 flex-1 overflow-x-auto">
        <FormProvider {...form}>
          <CollectPaymentForm
            isDeposit={isDeposit}
            encounterId={encounterId}
            onClose={onClose}
            onExpand={() => { setExpanded(!expanded); }}
            patient={selectedPatient}
          />
        </FormProvider>
        <PanelExtension
          className={clsx(
            'min-h-0 shrink-0 self-stretch bg-gray-50 rounded-r-lg transition-[width]',
            {
              'w-0': !expanded,
              'w-[640px]': expanded,
            },
          )}
        >
          <div className="flex h-full min-h-0 w-full flex-col populate-breakdown-table">
            <div className="shrink-0 w-full flex gap-4 p-4">
              <ToolTip title="Hide">
                <IconButton
                  size="small"
                  onClick={() => { setExpanded(false); }}
                >
                  <CollapseIcon className="!w-4 !h-4 !text-inherit rotate-180" />
                </IconButton>
              </ToolTip>
              <h3 className="font-semibold text-lg">Balance overdue explained</h3>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <UnpaidEncounters
                patientId={patientId}
                columns={totalBillReviewColumns}
              />
            </div>
          </div>
        </PanelExtension>
      </div>
    </Dialog>
  );
}
